import { translate } from "app/language/service"
import { getFormattedFileSize, isIcoFile, isImageFile, uploadFile } from "app/tools/file"
import { messageSVC } from "message/service"
import { state } from "ui/state"
import { IUiConfigUploadData, MAX_UI_CONFIG_FILE_UPLOAD_SIZE_IN_BYTES } from "../model"
import { uiConfigurationRPO } from "../repository"

export const uploadUiConfigFile = async (
  { error, selectedFiles, isUploading }: IUiConfigUploadData,
  setData: (newData: Partial<IUiConfigUploadData>) => void
): Promise<string> =>
  new Promise<string>(async (resolve, reject) => {
    if (error) {
      messageSVC.error(translate("upload.error.file.error"))
      return reject("We are currently uploading an image")
    }
    if (isUploading) {
      messageSVC.error(translate("upload.error.is.uploading"))
      return reject("We are currently uploading an image")
    }
    if (!selectedFiles || selectedFiles.length === 0) {
      messageSVC.error(translate("upload.error.no.file.selected"))
      return reject("No file is selected")
    }
    const file = selectedFiles[0]
    setData({ isUploading: true, error: "" })
    try {
      const response = await uploadFile(uiConfigurationRPO.getUploadUrl(), file, uploadParams => {
        clearTimeout(state.uiConfigUploadRefreshTimeout)
        state.uiConfigUploadRefreshTimeout = setTimeout(() => {
          const progressPercent = (100 * uploadParams.loadedBytes) / uploadParams.totalBytes
          setData({ progressPercent })
        }, 300)
      })
      resolve(response.data.relativePath)
    } catch (err) {
      clearTimeout(state.uiConfigUploadRefreshTimeout)
      console.error(err)
      setData({
        isUploading: false,
        error: translate("upload.error.serveur.request")
      })
      reject("Unable to upload ui configuration image, server error")
    }
  })

export const isUiConfigUploadFileValid = (files: FileList | null, isIco: boolean): boolean => {
  if (files && files.length > 0) {
    const file = files[0]
    if (isIco) {
      if (!isIcoFile(file.type)) {
        messageSVC.error(translate("admin.ui-configuration.upload.ico.error.extension"))
        return false
      }
    } else {
      if (!isImageFile(file.type)) {
        messageSVC.error(translate("admin.ui-configuration.upload.error.extension"))
        return false
      }
    }
    if (file.size > MAX_UI_CONFIG_FILE_UPLOAD_SIZE_IN_BYTES) {
      messageSVC.error(
        translate("upload.error.size", [
          getFormattedFileSize(file.size),
          getFormattedFileSize(MAX_UI_CONFIG_FILE_UPLOAD_SIZE_IN_BYTES)
        ])
      )
      return false
    }
  }
  return true
}

export const getDefaultUiConfigUploadState = (): IUiConfigUploadData => ({
  selectedFiles: null,
  error: "",
  isUploading: false,
  progressPercent: 0
})
